<template>
    <v-form v-model="valid">
        <div class="title">New Block Level Note/Alert</div>
        <v-textarea
            v-model="note.details"
            label="Details"
            :rules="[rules.required]"
            required>
        </v-textarea>
        <v-select
            v-model="note.berriesForecastNoteAlertCategoryId"
            :items="activeNoteAlertCategories"
            item-text="categoryName" item-value="berriesForecastNoteAlertCategoryId" 
            label="Categories"
            :rules="[rules.selectRequired]"
            required>
        </v-select>
        <v-radio-group v-model="note.alertLevel" row>
            <v-radio color="blue" label="Notice" :value="3"></v-radio>
            <v-radio color="orange" label="Caution" :value="2"></v-radio>
            <v-radio color="red" label="Critical" :value="1"></v-radio>
        </v-radio-group>
        <v-layout wrap align-center justfy-center> 
            <v-flex :xs12="note.photos.length==0" :xs6="note.photos.length==1" :xs4="note.photos.length > 1" class="text-xs-center"> 
                <v-btn fab color="primary" @click="addNewPhoto()" :loading="processingPhoto">
                    <v-icon style="display: inline-flex !important;">camera_alt</v-icon>
                </v-btn>
                <input
                    type="file"
                    style="display: none"
                    ref="newPhotoImage"
                    accept="image/*"
                    @change="handleNewPhotoCreation($event)"
                />
            </v-flex>   
            <v-flex :xs6="note.photos.length ==1" :xs4="note.photos.length> 1" :key="index" v-for="(photo, index) in note.photos">
                <img class="thumbnail" :src="photoSrc(photo)" />
            </v-flex>
        </v-layout>
        <v-btn @click="clear" color="error">Clear</v-btn>
        <v-btn @click="save" color="primary" :disabled="!valid">Save</v-btn>
    </v-form>
</template>

<script>


import UtilService from '@/services/utilService.js';
import moment from 'moment';
import forecastingApi from '@/api/forecastingApi'
import validationRules from "@/helpers/validationrules";

import { mapState, mapActions } from "vuex"

export default {
    components: {
 
    },
    props: {
        variety: {
            type: Object,
            required: true
        }
    },
    data(){
        return {
            rules: validationRules.rules,
            row: 1,
            select: 1,
            details: "",
            processingPhoto: false,
            note: {
                photos: [],
                details: "",
                berriesForecastNoteAlertCategoryId: null,
                alertLevel: 2
            },
            imageAuthToken: null,
            valid: false    
        };
    },
    computed: {
        ...mapState("ForecastingStore", [
            "noteAlertCategories"
        ]),
        activeNoteAlertCategories() {
            return this.noteAlertCategories ? this.noteAlertCategories.filter(c=> c.active): [];
        }
    },
    methods: {
        ...mapActions("ForecastingStore", [
            "addNote",
            "updateData"
        ]),
        clear() {
            this.note = {
                photos: [],
                details: "",
                berriesForecastNoteAlertCategoryId: null,
                alertLevel: 2
            };
        },
        async save() {
            this.note.raisedAt = moment().utc().format();
            this.note.cropLocationId = this.variety.cropLocationId;
            this.note.varietyId = this.variety.varietyId;
            this.note.raisedByName = this.$store.getters["UserStore/getFullName"];
            var selectedCategory = this.activeNoteAlertCategories.find(c=> c.berriesForecastNoteAlertCategoryId == this.note.berriesForecastNoteAlertCategoryId);
            this.note.categoryName = selectedCategory.categoryName;

            await this.addNote(this.note);
            await this.updateData();

            this.clear();
        },
        async loadImageAuthToken(){
            this.imageAuthToken = await this.$store.dispatch("UserStore/acquireImageAuthToken");
        },
        addNewPhoto() {
            if(this.processingPhoto){
                return;
            }

            this.$refs["newPhotoImage"].click();
        },
        async handleNewPhotoCreation(event){
            let newPhoto = await this.getFileData(event);
            this.$refs["newPhotoImage"].value = null; // Reset selector

            if(newPhoto == null){
                return;
            }

            try {
                this.processingPhoto = true;
                var photo  = {
                    uploadedAt: new Date().toISOString(),
                    photoExtension: newPhoto.extension,
                    photoMimeType: newPhoto.mimeType,
                    photoBlob: newPhoto.blob,
                    photoBase64: newPhoto.photoBase64
                };
         
                var photoName = await forecastingApi.uploadNotePhoto(photo);

                photo.photoName = photoName;

                this.note.photos.push(photo);
            } catch(err){
                console.log(err);
                this.showPhotoUploadError("Unable to upload photos currently. Ensure you are connected to the internet and try again.");
            } finally {
                this.processingPhoto = false;
            }
        },
        showPhotoUploadError(p_errorMessage){
            alert(p_errorMessage);
        },
        photoSrc(photo) {
            if (photo.photoBase64) {
                return photo.photoBase64;
            } else if (photo.photoName) {
            return process.env.VUE_APP_API_URI + '/forecasting/photoData?' + [
                    'PhotoName=' + photo.photoName,
                    'Token=' + this.imageAuthToken
                ].join('&');
            } else {
                return '';
            }
        },
        async toBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            })
        },
        async getFileData(event) {
            let photo = null;

            this.processingPhoto = true;

            const files = event.target.files;
            if(files[0] !== undefined) {
                if(files[0].name.lastIndexOf('.') > 0) {
                    let fileNameSplit = files[0].name.split('.');
                    let extension = fileNameSplit[fileNameSplit.length - 1];
                    let mimeType = files[0].type;

                    let image = await UtilService.loadFileToImage(files[0]);
                    let maxFileSize = 2 * 1024 * 1024; // 2mb
                    let blob = await UtilService.resizeUntilBelow(image, mimeType, maxFileSize);
                    var photoBase64 = await this.toBase64(blob);
                    if(blob != null){
                        photo = {
                            extension,
                            mimeType,
                            blob,
                            photoBase64
                        };
                    }
                }
            }

            this.processingPhoto = false;

            return photo;
        },

    },
    async created(){
        await this.loadImageAuthToken();
    }
}
</script>

<style>

.thumbnail {
    max-height: 200px;
    max-width: 100%;
}

</style>